import React, { useMemo, useState } from "react";
import { useQuery as useApolloQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import useMutation from "../../../../../hooks/useMutation";
import { UPDATE_SHIPMENT_MUTATION } from "../../../../../constants/graphql/mutations";
import {
  prepareShipmentUpdateInput,
  getBillingQuantityOptions,
  getBillingQuantityLabels,
  billingQuantitiesInput,
} from "../../../../../utils/billingHelpers";
import {
  HUNDREDWEIGHT,
  BY_THE_UNIT_ITEM,
  SQUARE_FOOT_LOCATION,
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "../../../../project/settings/utils/billingTypes";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "../../../../../constants/graphql/mutations";
import { INVENTORY_REQUEST_QUERY } from "../../../../../constants/graphql/queries";
import LocationDimensionsBreakdown from "./LocationDimensionsBreakdown";

const styles = {
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "text.secondary",
  },
  subtitle: {
    mb: 4,
  },
  toggleButtonGroup: {
    "& .MuiToggleButton-root": {
      flex: 1,
      "&.Mui-selected": {
        backgroundColor: "#1976d2",
        color: "white",
        "&:hover": {
          backgroundColor: "#1565c0",
        },
      },
    },
  },
};

export default function BillingQuantitiesModal({
  type,
  onClose,
  billingType,
  shipment,
  onSave,
  onBack,
  palletTypes,
  unitTypes,
  // Editing saved quantities after the fact: the modal can be cancelled and
  // every quantity can be set back to zero. Picking and receiving flows leave
  // this off so quantities stay required.
  allowClear = false,
}) {
  const metadata = shipment?.billingQuantityInputMetadata || {};
  const isAutomatic = metadata?.mode === "automatic";
  const options = getBillingQuantityOptions(billingType, palletTypes, unitTypes);
  const labels = getBillingQuantityLabels(billingType, type);
  const isLocationDimensionsReadOnly = billingType === SQUARE_FOOT_LOCATION;
  const { data: locationDimensionsData } = useApolloQuery(INVENTORY_REQUEST_QUERY, {
    variables: { id: shipment?.id },
    skip: !shipment?.id || type !== "Outbound" || billingType !== SQUARE_FOOT_LOCATION,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const normalizeBillingQuantityType = (palletType) => {
    if (billingType === BY_THE_UNIT_ITEM && palletType === "per_item") {
      return "standard_unit";
    }

    return palletType;
  };

  const normalizeBillingQuantities = (list = []) =>
    list.map((pallet) => ({
      ...pallet,
      pallet_type: normalizeBillingQuantityType(pallet.pallet_type),
    }));

  const savedPallets = normalizeBillingQuantities(
    (Array.isArray(shipment?.billingQuantities) ? shipment.billingQuantities : []).map(
      (bq) => ({ pallet_type: bq.quantity_type, pallet_count: bq.quantity_count })
    )
  );
  const visibleOptionValues = options.map(([value]) => value);
  const hiddenStandardUnitQuantity =
    billingType === BY_THE_UNIT_ITEM && !visibleOptionValues.includes("standard_unit")
      ? savedPallets
          .filter((pallet) => pallet.pallet_type === "standard_unit")
          .reduce((sum, pallet) => sum + Number(pallet.pallet_count || 0), 0)
      : 0;

  const [pallets, setPallets] = useState(savedPallets);
  const visiblePallets = pallets.filter((pallet) => visibleOptionValues.includes(pallet.pallet_type));
  const [totalWeight, setTotalWeight] = useState(shipment.totalWeight || "");
  const [isWeightFocused, setIsWeightFocused] = useState(false);
  const [weightUnits, setWeightUnits] = useState(
    shipment.weightUnits && shipment.weightUnits !== "" ? shipment.weightUnits : null
  );
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const locationDimensionsInventoryItems =
    locationDimensionsData?.inventoryRequest?.inventoryItems || shipment?.inventoryItems || [];

  const suggestedWeight = (shipment?.shipmentItems || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.asset?.weight) || 0),
    0
  );
  const normalizePallets = (list) =>
    [...list]
      .filter((p) => Number(p.pallet_count || 0) > 0)
      .map((p) => ({ t: String(normalizeBillingQuantityType(p.pallet_type)), c: Number(p.pallet_count) }))
      .sort((a, b) => a.t.localeCompare(b.t));

  const isValid =
    billingType === HUNDREDWEIGHT
      ? totalWeight > 0 && weightUnits !== null && weightUnits !== ""
      : visiblePallets.some((pallet) => pallet.pallet_type && pallet.pallet_count > 0);
  const hasSavedQuantities = savedPallets.some((pallet) => Number(pallet.pallet_count || 0) > 0);
  const canSave =
    isValid || (allowClear && billingType !== HUNDREDWEIGHT && hasSavedQuantities);
  const hasQuantityInputs = billingType === HUNDREDWEIGHT || options.length > 0;
  const canClose = allowClear || isLocationDimensionsReadOnly || isValid || !hasQuantityInputs;

  const handleSave = async () => {
    const editablePallets = billingType === BY_THE_UNIT_ITEM ? visiblePallets : pallets;
    const updateData = billingQuantitiesInput(billingType, totalWeight, weightUnits, editablePallets);

    if (isAutomatic) {
      const userEdited =
        JSON.stringify(normalizePallets(pallets)) !==
        JSON.stringify(normalizePallets(savedPallets));
      updateData.billingQuantityInputMetadata = {
        mode: "automatic",
        status: userEdited ? "overridden" : "accepted",
      };
    }

    switch (type) {
      case "Inbound": {
        const input = prepareShipmentUpdateInput(shipment, updateData);
        await updateShipment({
          variables: { id: shipment.id, input },
        });
        break;
      }
      case "Outbound": {
        const { billingType: _, ...inventoryRequestInput } = updateData;
        await updateInventoryRequest({
          variables: { id: shipment.id, input: inventoryRequestInput },
        });
        break;
      }
    }
    onSave();
  };

  const handleCloseAttempt = () => {
    if (canClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleCloseAttempt}
      disableEscapeKeyDown={!canClose}
      sx={{
        "& .MuiDialog-paper": {
          width: isLocationDimensionsReadOnly ? "520px" : "500px",
          maxWidth: "100%",
        },
      }}
    >
      <DialogContent>
        {canClose && (
          <IconButton onClick={handleCloseAttempt} sx={styles.closeButton} title="Close">
            <CloseIcon />
          </IconButton>
        )}
        <Box>
          <Typography
            fontSize={22}
            fontWeight={500}
            color="text.primary"
            align="center"
            sx={{ mb: labels.subtitle ? 2 : 4 }}
          >
            {labels.title}
          </Typography>
          {labels.subtitle && (
            <Typography fontSize={15} color="text.secondary" align="center" sx={styles.subtitle}>
              {labels.subtitle}
            </Typography>
          )}
          {hiddenStandardUnitQuantity > 0 && (
            <Typography fontSize={14} color="text.secondary" align="center" sx={{ mt: -2, mb: 3 }}>
              {hiddenStandardUnitQuantity.toLocaleString()} standard{" "}
              {hiddenStandardUnitQuantity === 1 ? "unit" : "units"} inputted
            </Typography>
          )}
          {isAutomatic && savedPallets.length > 0 && (
            <Typography fontSize={14} color="primary" align="center" sx={{ mb: 3 }}>
              System auto-calculated billable quantities from operational item data. Please review
              and edit if needed.
            </Typography>
          )}

          {billingType === HUNDREDWEIGHT ? (
            <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={10}>
              {suggestedWeight > 0 && (
                <Box width="80%" mb={3}>
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Suggested total weight: {suggestedWeight.toLocaleString()} lbs
                  </Alert>
                </Box>
              )}
              <Box display="flex" alignItems="flex-end" gap={2} width="80%">
                <Box width="50%">
                  <TextField
                    label={labels.quantityLabel}
                    type="text"
                    inputMode="numeric"
                    value={
                      isWeightFocused
                        ? totalWeight
                        : totalWeight !== ""
                          ? Number(totalWeight).toLocaleString()
                          : ""
                    }
                    onFocus={() => setIsWeightFocused(true)}
                    onBlur={() => setIsWeightFocused(false)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                        setTotalWeight(raw);
                      }
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box width="50%">
                  <ToggleButtonGroup
                    value={weightUnits}
                    exclusive
                    onChange={(e, newValue) => setWeightUnits(newValue)}
                    size="small"
                    fullWidth
                    sx={styles.toggleButtonGroup}
                  >
                    <ToggleButton value="Lbs">LBS</ToggleButton>
                    <ToggleButton value="Kgs">KGS</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>
          ) : isLocationDimensionsReadOnly ? (
            <LocationDimensionsBreakdown inventoryItems={locationDimensionsInventoryItems} />
          ) : (
            <Box mt={4} mb={6}>
              {options.map(([value, label]) => {
                const palletData = pallets.find((p) => p.pallet_type === value) || {
                  pallet_type: value,
                  pallet_count: 0,
                };

                const projectSettings = shipment?.project?.settings || {};
                const timeIncrementMinutes = parseInt(
                  projectSettings.time_increment_minutes || 60,
                  10
                );
                const isTimeBased = [
                  TIME_AND_MATERIALS_BLENDED,
                  TIME_AND_MATERIALS_ITEMIZED,
                ].includes(billingType);

                let step = 1;
                let isDisabled = false;
                let displayLabel = label;

                if (isTimeBased) {
                  if (timeIncrementMinutes === 15) step = 0.25;
                  else if (timeIncrementMinutes === 20) step = 0.33;
                  else if (timeIncrementMinutes === 30) step = 0.5;

                  if (timeIncrementMinutes !== 60) {
                    isDisabled = true;
                    displayLabel = `${label} (per ${timeIncrementMinutes} mins)`;
                  }
                }

                const handleIncrement = () => {
                  const currentValue = Number(palletData.pallet_count || 0);
                  const newQuantity = currentValue + step;
                  const rounded = Math.round(newQuantity * 100) / 100;

                  const existingIndex = pallets.findIndex((p) => p.pallet_type === value);
                  if (existingIndex >= 0) {
                    setPallets(
                      pallets.map((item, i) =>
                        i === existingIndex ? { ...item, pallet_count: rounded } : item
                      )
                    );
                  } else {
                    setPallets([...pallets, { pallet_type: value, pallet_count: rounded }]);
                  }
                };

                const handleDecrement = () => {
                  const currentValue = Number(palletData.pallet_count || 0);
                  const newQuantity = Math.max(0, currentValue - step);
                  const rounded = Math.round(newQuantity * 100) / 100;

                  const existingIndex = pallets.findIndex((p) => p.pallet_type === value);
                  if (existingIndex >= 0) {
                    setPallets(
                      pallets.map((item, i) =>
                        i === existingIndex ? { ...item, pallet_count: rounded } : item
                      )
                    );
                  }
                };

                const handleChange = (event) => {
                  const newQuantity = event.target.value;
                  const existingIndex = pallets.findIndex((p) => p.pallet_type === value);
                  if (existingIndex >= 0) {
                    setPallets(
                      pallets.map((item, i) =>
                        i === existingIndex ? { ...item, pallet_count: newQuantity } : item
                      )
                    );
                  } else {
                    setPallets([...pallets, { pallet_type: value, pallet_count: newQuantity }]);
                  }
                };

                return (
                  <Box
                    key={value}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={2}
                    borderBottom="1px solid"
                    borderColor="divider"
                  >
                    <Typography variant="body1" fontWeight={400}>
                      {displayLabel}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="outlined"
                        onClick={handleDecrement}
                        sx={{ minWidth: 40, height: 40 }}
                      >
                        –
                      </Button>
                      <TextField
                        disabled={isDisabled}
                        value={palletData.pallet_count ?? 0}
                        onChange={handleChange}
                        onFocus={(e) => e.target.select()}
                        label="Quantity"
                        size="small"
                        inputProps={{ style: { textAlign: "center" } }}
                        sx={{
                          width: 90,
                          "& .MuiInputLabel-root": {
                            left: "50%",
                            top: "50%",
                            transform: "translateX(-50%) translateY(-50%)",
                            transformOrigin: "center",
                          },
                          "& .MuiInputLabel-shrink": {
                            left: "50%",
                            top: 0,
                            transform: "translateX(-50%) translateY(-11px) scale(0.75)",
                            transformOrigin: "center",
                          },
                          "& .MuiOutlinedInput-notchedOutline legend": {
                            textAlign: "center",
                          },
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleIncrement}
                        sx={{ minWidth: 40, height: 40 }}
                      >
                        +
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {(onBack || allowClear || !isLocationDimensionsReadOnly) && (
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {onBack && (
                <Button variant="outlined" onClick={onBack}>
                  Back
                </Button>
              )}
              {allowClear && (
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
              )}
              {!isLocationDimensionsReadOnly && (
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!canSave}>
                  Save
                </Button>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
